import React from "react";
import ReactDOM from "react-dom";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import {
    Event,
    LobbyOption,
    PlayerGameRequestResult,
    RankedPlayer,
    Room,
    RoomAccessibility,
} from "@uno/protocol";
import { AppContext } from "context";
import useMediaQuery from "hook/useMediaQuery";
import getPlayerGamePath from "utils/PlayerGameNavigation";
import useStorePlayer from "store/useStorePlayer";
import useGameEndAnimation from "scene/Room/hook/useGameEndAnimation";
import GuestSignupPrompt from "./GuestSignupPrompt";
import RankingView from "./RankingView";
import RulesView from "./RulesView";
import WeeklyImpact from "./WeeklyImpact";

import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/navigation";
import "swiper/css/effect-coverflow";
import "./index.scss";

type Props = { room: Room };

type View = "ranking" | "rules";

const VIEWS: { id: View; labelKey: string }[] = [
    { id: "ranking", labelKey: "room.view_ranking" },
    { id: "rules", labelKey: "room.view_rules" },
];

const Ranking: React.FC<Props> = ({ room }) => {
    const { socket } = React.useContext(AppContext);
    const { t } = useTranslation();
    const navigate = useNavigate();

    const containerRef = React.useRef<HTMLDivElement>(null);
    const [activeView, setActiveView] = React.useState<View>("ranking");
    const [impactDismissed, setImpactDismissed] = React.useState(false);
    const myPlayerId = useStorePlayer((state) => state.player?.id);
    const hasSavedAccount = useStorePlayer((state) => state.player?.hasSavedAccount);

    const hasMobileViewport = useMediaQuery(
        "(max-width: 767px), (orientation: portrait) and (max-width: 900px), (orientation: landscape) and (max-width: 900px) and (max-height: 500px)",
    );
    const hasCompactViewport = useMediaQuery(
        "(orientation: landscape) and (min-width: 700px) and (max-height: 600px)",
    );
    const hasShortMobileViewport = useMediaQuery("(max-height: 720px)");
    const hasDenseMobileViewport = useMediaQuery("(max-height: 430px)");
    const isCompactLandscape = hasCompactViewport;
    const isMobile = hasMobileViewport && !isCompactLandscape;
    const isDenseMobile = isMobile && hasDenseMobileViewport;
    const isCompactMobile = isMobile && hasShortMobileViewport && !isDenseMobile;
    const usesFixedLayout = isMobile || isCompactLandscape;
    const usesCompactSpacing = isCompactLandscape || isCompactMobile || isDenseMobile;

    const players: RankedPlayer[] = room.gameState.ranking?.players ?? [];
    const myPlayer = players.find((p) => p.id === myPlayerId);
    const myImpactRaw = myPlayer?.weeklyImpact;
    const myImpact = myImpactRaw && myImpactRaw.countedInDaily ? myImpactRaw : undefined;
    const showGuestPrompt = hasSavedAccount === false;
    const showWeeklyImpact = hasSavedAccount === true && !!myImpact;
    const showEndModal = !impactDismissed && (showGuestPrompt || showWeeklyImpact);

    const { skip } = useGameEndAnimation(containerRef, players);

    const handleHome = (e: React.MouseEvent) => {
        e.stopPropagation();
        navigate("/");
    };

    const handlePlayAgain = (e: React.MouseEvent) => {
        e.stopPropagation();

        const options: LobbyOption = {
            accessibility: room.accessibility,
        };

        if(room.accessibility === RoomAccessibility.Private) {
            options.id = room.id;
            options.rules = room.game.rules;
        }

        socket?.emit(Event.PlayerGameRequest, options, (result: PlayerGameRequestResult) => {
            navigate(getPlayerGamePath(result));
        });
    };

    return ReactDOM.createPortal(
        <div
            ref={containerRef}
            onClick={activeView === "ranking" ? skip : undefined}
            className="fixed inset-0 z-[200] bg-black/80 animate-[fadeIn_0.3s_ease-out]"
        >
            <div
                className={`ge-layout absolute inset-0 min-h-full items-center justify-between overflow-x-hidden panel-scrollbar ${
                    usesFixedLayout
                        ? `grid grid-cols-[minmax(0,1fr)] grid-rows-[auto_minmax(0,1fr)_auto] justify-items-center overflow-hidden ${
                            isDenseMobile ? "gap-1" : "gap-2"
                        }`
                        : "flex flex-col overflow-y-auto gap-6"
                }`}
                style={{
                    paddingTop: usesFixedLayout
                        ? `max(${isDenseMobile ? "0.25rem" : "0.5rem"}, var(--safe-top))`
                        : "calc(2.5rem + var(--safe-top))",
                    paddingBottom: usesFixedLayout
                        ? `max(${isDenseMobile ? "0.25rem" : "0.5rem"}, var(--safe-bottom))`
                        : "calc(2.5rem + var(--safe-bottom))",
                }}
            >
            {/* View toggle */}
            <div
                className="ge-header shrink-0 relative flex items-center gap-1 p-1 rounded-full bg-white/10 backdrop-blur-sm border border-white/10 justify-self-center"
                onClick={(e) => e.stopPropagation()}
            >
                {VIEWS.map((view) => (
                    <button
                        key={view.id}
                        onClick={() => setActiveView(view.id)}
                        className={`relative rounded-full font-changa-one tracking-[0.2em] transition-colors cursor-pointer ${
                            isDenseMobile
                                ? "px-3 py-1 text-[10px]"
                                : usesCompactSpacing
                                    ? "px-4 py-1.5 text-xs"
                                    : "px-5 py-2 text-xs md:text-sm"
                        } ${
                            activeView === view.id
                                ? "text-white"
                                : "text-white/40 hover:text-white/60"
                        }`}
                    >
                        {activeView === view.id && (
                            <motion.div
                                layoutId="ge-view-indicator"
                                className="absolute inset-0 rounded-full bg-white/15"
                                transition={{ type: "spring", stiffness: 400, damping: 30 }}
                            />
                        )}
                        <span className="relative z-10">{t(view.labelKey)}</span>
                    </button>
                ))}
            </div>

            {/* Content */}
            <div
                className={`ge-content w-full flex items-center justify-center ${
                    usesFixedLayout ? "min-w-0 min-h-0 self-stretch" : "shrink-0"
                }`}
                onClick={(e) => e.stopPropagation()}
            >
                <AnimatePresence mode="wait" initial={false}>
                    {activeView === "ranking" ? (
                        <motion.div
                            key="ranking"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.15 }}
                            className="w-full"
                        >
                            <RankingView
                                players={players}
                                myPlayerId={myPlayerId}
                                isMobile={isMobile}
                                isCompact={isCompactLandscape || isCompactMobile}
                                isDense={isDenseMobile}
                            />
                        </motion.div>
                    ) : (
                        <motion.div
                            key="rules"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.15 }}
                            className="w-full max-w-[560px] px-5"
                        >
                            <RulesView />
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>

            {/* Buttons */}
            <div className={`ge-buttons shrink-0 w-full max-w-md justify-self-center flex items-center ${
                usesCompactSpacing ? "px-3 gap-2" : "px-5 gap-3"
            }`}>
                <button
                    onClick={handleHome}
                    aria-label={t("room.home")}
                    className={`game-btn game-btn-muted shrink-0 rounded-full flex items-center justify-center cursor-pointer ${
                        isDenseMobile
                            ? "w-10 h-10"
                            : usesCompactSpacing
                                ? "w-11 h-11"
                                : "w-14 h-14"
                    }`}
                >
                    <svg className="relative z-10 w-4 h-4 fill-[#1f2937]" viewBox="0 0 25 25">
                        <path d="M487.148,1053.48 L492.813,1047.82 C494.376,1046.26 494.376,1043.72 492.813,1042.16 C491.248,1040.59 488.712,1040.59 487.148,1042.16 L481.484,1047.82 L475.82,1042.16 C474.257,1040.59 471.721,1040.59 470.156,1042.16 C468.593,1043.72 468.593,1046.26 470.156,1047.82 L475.82,1053.48 L470.156,1059.15 C468.593,1060.71 468.593,1063.25 470.156,1064.81 C471.721,1066.38 474.257,1066.38 475.82,1064.81 L481.484,1059.15 L487.148,1064.81 C488.712,1066.38 491.248,1066.38 492.813,1064.81 C494.376,1063.25 494.376,1060.71 492.813,1059.15 L487.148,1053.48" transform="translate(-469, -1041)" />
                    </svg>
                </button>
                <button
                    onClick={handlePlayAgain}
                    className={`game-btn flex-1 rounded-[60px] font-changa-one uppercase tracking-wide text-[#195b00] cursor-pointer ${
                        isDenseMobile
                            ? "h-10 text-xs"
                            : usesCompactSpacing
                                ? "h-11 text-sm"
                                : "h-14 text-lg"
                    }`}
                >
                    <span className="relative z-10">{t("room.play_again")}</span>
                </button>
            </div>
            </div>

            {/* Guest signup reminder or weekly impact — revealed after the ranking animation */}
            {showEndModal && (
                <div
                    className="ge-weekly-impact-slot fixed inset-0 z-[450] flex items-center justify-center px-4"
                    onClick={(e) => e.stopPropagation()}
                >
                    <div
                        className="absolute inset-0 bg-black/55 backdrop-blur-[2px]"
                        onClick={() => setImpactDismissed(true)}
                    />
                    <div className="relative z-10 w-full max-w-md">
                        {showGuestPrompt ? (
                            <GuestSignupPrompt onDismiss={() => setImpactDismissed(true)} />
                        ) : myImpact ? (
                            <WeeklyImpact
                                impact={myImpact}
                                onContinue={() => setImpactDismissed(true)}
                                onOpenLeaderboard={() => navigate("/leaderboard")}
                            />
                        ) : null}
                    </div>
                </div>
            )}
        </div>,
        document.body
    );
};

export default Ranking;
